import { useCallback, useEffect, useReducer, useRef } from "react";
import { useGroupQuery, type GroupQueryState, type UseGroupQueryOptions } from "#app/lib/hooks/use-group-query";
import { useLocalGroupPages } from "#app/lib/hooks/use-local-group-pages";

// Re-export types so existing consumers don't need to change imports.
export type { GroupQueryState } from "#app/lib/hooks/use-group-query";

// ---------------------------------------------------------------------------
// GroupQueryProvider — renderless component wrapper around useGroupQuery
// ---------------------------------------------------------------------------

export type GroupQueryProviderProps<TData> = UseGroupQueryOptions<TData>;

/**
 * Renderless component that runs a paginated query for a single group
 * and reports its state upward via `onStateChange`. Mount one per group.
 *
 * Delegates entirely to `useGroupQuery` — this component exists so that
 * grouped tables can mount/unmount one query per group via JSX.
 */
export function GroupQueryProvider<TData>(props: GroupQueryProviderProps<TData>) {
  useGroupQuery(props);
  return null;
}

// ---------------------------------------------------------------------------
// LocalGroupProvider — renderless local twin of GroupQueryProvider
// ---------------------------------------------------------------------------

export type LocalGroupProviderProps<TData> = {
  /** Unique key for this group. */
  groupKey: string;
  /** The group's fully-materialized rows. */
  rows: TData[];
  /** Semantic identity of `rows` — flips on filter/sort/search changes and
   *  resets the group's window (see `useLocalGroupPages`). */
  contextKey: string;
  /** Report this group's current state to the parent. */
  onStateChange: (key: string, state: GroupQueryState<TData>) => void;
};

/**
 * Pages a fully-materialized in-memory dataset and reports its state upward
 * through the same contract as `GroupQueryProvider`. Mount one per group;
 * tables consume the state via `useGroupStates`/`getGroupProps` exactly as
 * they do for query-backed groups.
 */
export function LocalGroupProvider<TData>({
  groupKey,
  rows,
  contextKey,
  onStateChange,
}: LocalGroupProviderProps<TData>) {
  const state = useLocalGroupPages(rows, contextKey);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  useEffect(() => {
    onStateChangeRef.current(groupKey, state);
  }, [groupKey, state]);
  return null;
}

// ---------------------------------------------------------------------------
// useGroupStates — shared state manager for per-group queries
// ---------------------------------------------------------------------------

/**
 * Manages a Map of per-group query states, used by table components that
 * render GroupQueryProvider instances.
 */
export function useGroupStates<TData>() {
  const [states, dispatch] = useReducer(
    (prev: Map<string, GroupQueryState<TData>>, action: { key: string; state: GroupQueryState<TData> }) => {
      // Bail out if the state hasn't actually changed — returning the same
      // reference prevents a re-render cascade through the entire table tree.
      const existing = prev.get(action.key);
      if (
        existing &&
        existing.data === action.state.data &&
        existing.total === action.state.total &&
        existing.loading === action.state.loading &&
        existing.dataOffset === action.state.dataOffset &&
        existing.variablesKey === action.state.variablesKey
      ) {
        return prev;
      }
      const next = new Map(prev);
      next.set(action.key, action.state);
      return next;
    },
    new Map<string, GroupQueryState<TData>>(),
  );
  const registerState = useCallback(
    (key: string, state: GroupQueryState<TData>) => dispatch({ key, state }),
    [],
  );

  /**
   * Extract DataTableGroup-compatible fields from state for a given group key.
   * Spread into each group object so consumers don't have to wire up fields.
   */
  const getGroupProps = useCallback(
    (key: string) => {
      const state = states.get(key);
      return {
        data: (state && !state.loading ? state.data : []) as TData[],
        loading: state?.loading ?? true,
        dataOffset: state?.dataOffset ?? 0,
        fetchPage: state?.fetchPage,
        variablesKey: state?.variablesKey,
      };
    },
    [states],
  );

  return { states, registerState, getGroupProps };
}
